/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import 'intersection-observer';
import pick from 'lodash/pick';
import {intersectionOptions} from '../options/intersection.options';

/**
 * Intersection directive
 * Used to detecting visibility of an element, or the relative visibility of two elements in relation to each other.
 *
 * For details, see @link [Intersection Observer](https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
 */
export const intersectionDirective = {
  /**
   * Intersection options container
   */
  options: intersectionOptions,

  /**
   * A map hold all observer instances all callbacks related for each element
   *
   * @type {Map<string, {
   *  callbacks: {[key: string]: (entry: IntersectionObserverEntry) => void},
   *  observer: IntersectionObserver
   * }}
   */
  observers: new Map(),

  /**
   * Initialize new intersection observer for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   * @param {object}      options Intersection options
   */
  init(element, options = {}) {
    const intersectionInit = pick(options, ['root', 'rootMargin', 'threshold', 'trackVisibility', 'delay']);
    const callbacks = pick(options, ['intersect', 'enter', 'leave']);

    if (Object.keys(callbacks).length === 0) {
      console.warn('There is no reason to observe element intersection without any callbacks. Skipping...');
    } else {
      const observer = new IntersectionObserver((entries) => this.onIntersection(entries), intersectionInit);

      observer.observe(element);
      this.observers.set(element.ambid, {observer, callbacks});
    }
  },

  /**
   * Destroy initialized intersection observer for the given element
   *
   * @param {HTMLElement} element HTML DOM element
   */
  destroy(element) {
    const ambid = element.ambid;

    if (this.observers.has(ambid)) {
      const {observer} = this.observers.get(ambid);

      observer.disconnect();
      this.observers.delete(ambid);
    }
  },

  /**
   * Fires when intersection of registered elements change
   *
   * @protected
   *
   * @param {IntersectionObserverEntry[]} entries List of intersection observer entry
   */
  onIntersection(entries) {
    entries.forEach((entry) => {
      const {callbacks, observer} = this.observers.get(entry.target.ambid);
      const minThreshold = Array.isArray(observer.thresholds) ? Math.min(...observer.thresholds) : observer.thresholds;

      if (typeof callbacks.intersect === 'function') {
        callbacks.intersect(entry);
      }

      if (entry.intersectionRatio <= minThreshold) {
        if (typeof callbacks.leave === 'function') {
          callbacks.leave(entry);
        }
      } else if (typeof callbacks.enter === 'function') {
        callbacks.enter(entry);
      }
    });
  },
};
